import React, { useRef, useState } from 'react';
import { FaPlay, FaPause } from 'react-icons/fa';
import { getRecordingURL } from './twilioCall';

const formatDuration = (secs) => {
  if (!secs || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

function CallAudioPlayer({ callUuid }) {
  const audioRef = useRef(null);
  const trackRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  const ensureUrl = async () => {
    if (url) return url;
    setLoading(true);
    try {
      const signed = await getRecordingURL(callUuid);
      setUrl(signed);
      return signed;
    } catch (e) {
      setError('Failed to load recording');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (playing) {
      audio.pause();
      return;
    }
    if (!url) {
      const u = await ensureUrl();
      if (!u) return;
      setTimeout(() => audioRef.current?.play(), 0);
    } else {
      audio.play();
    }
  };

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setElapsed(audio.currentTime || 0);
    if (Number.isFinite(audio.duration)) setTotal(audio.duration);
  };

  const seekToClientX = (clientX) => {
    const audio = audioRef.current;
    const track = trackRef.current;
    if (!audio || !track || !total) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    audio.currentTime = ratio * total;
    setElapsed(ratio * total);
  };

  const pct = total > 0 ? Math.min(100, Math.max(0, (elapsed / total) * 100)) : 0;

  if (error) return <span style={{ fontSize: '12px', color: '#dc2626' }}>{error}</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '220px' }}>
      <button
        type="button"
        onClick={togglePlay}
        disabled={loading}
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '9999px',
          background: '#111827',
          color: '#fff',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: loading ? 'wait' : 'pointer',
          padding: 0,
          flexShrink: 0
        }}
      >
        {playing ? <FaPause size={10} /> : <FaPlay size={10} />}
      </button>
      <div
        ref={trackRef}
        onClick={(e) => seekToClientX(e.clientX)}
        style={{
          flex: 1,
          position: 'relative',
          height: '6px',
          background: '#e5e7eb',
          borderRadius: '9999px',
          cursor: total ? 'pointer' : 'default'
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${pct}%`,
            background: '#2563eb',
            borderRadius: '9999px'
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `${pct}%`,
            top: '50%',
            width: '12px',
            height: '12px',
            borderRadius: '9999px',
            background: '#2563eb',
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}
        />
      </div>
      <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '72px', textAlign: 'right' }}>
        {formatDuration(Math.floor(elapsed))} / {formatDuration(Math.floor(total))}
      </span>
      <audio
        ref={audioRef}
        src={url || undefined}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setElapsed(0);
        }}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onTimeUpdate}
        preload="none"
      />
    </div>
  );
}

export default CallAudioPlayer;
